/**
 * Adapptio Editor
 *
 * @package editor
 * @copyright 2021-2025 Hexio a.s. <contact@hexio.io> (hexio.io) and contributors.
 * @license Apache-2.0
 *
 * See LICENSE file distributed with this source code for more information.
 */

import React, { FC, useCallback, useMemo, useState } from "react";
import { BEM, T, useTranslate } from "@adapptio/lib-components";
import {
	createEmptyScope,
	ISchemaFlowNodeListModel,
	ISchemaFlowNodeModel,
	ISchemaFlowNodeTypeDefinitionMap,
	TGenericModelNode
} from "@adapptio/lib-blueprint";
import { useBlueprintModel } from "../../hooks";
import { InvalidModelNode } from "./InvalidModelNode";
import { PanelNote } from "../Panel";
import { termsEditorInspector } from "../../../terms/inspector";
import { Icon, ICON_SIZE } from "../Icon";
import { ToolbarCommandButton } from "../Toolbar/ToolbarCommandButton";
import { IInspectorControlProps } from "./InspectorTypes";
import { FieldGroup } from "../Fields/FieldGroup";
import { FieldInlineLabel } from "../Fields/FieldInlineLabel";
import { TABBED_CONTAINER_TYPE, TabbedContainer } from "../TabbedContainer";
import { Tabs, TABS_STYLE } from "../Tabs";
import { ScopeViewer } from "../ScopeViewer/ScopeViewer";
import { FieldHeader } from "../Fields/FieldHeader";
import { FieldLabel } from "../Fields/FieldLabel";
import { FieldRow } from "../Fields/FieldRow";
import { FIELD_CONTAINER_STYLE, FieldContainer } from "../Fields/FieldContainer";
import { ToolbarButton } from "../Toolbar/ToolbarButton";
import { Inspector } from "../../../inspector";
import { InspectorTitle } from "./InspectorTitle";
import { REFERENCE_TYPE } from "../../../modules/Core/ReferenceGuide/ReferenceGuideTypes";
import { InspectorControlString } from "../../../inspector/controls/InspectorControlString/InspectorControlString";

export interface IInspectFlowNodeProps
	extends IInspectorControlProps<ISchemaFlowNodeModel<ISchemaFlowNodeTypeDefinitionMap>> {
	overrideLabel?: string;
	overrideIcon?: string;
	nodeList: ISchemaFlowNodeListModel<ISchemaFlowNodeTypeDefinitionMap>;
}

export const InspectFlowNode: FC<IInspectFlowNodeProps> = ({
	model: _model,
	nodeList,
	// depth,
	// path,
	overrideLabel,
	overrideIcon
}) => {
	const baseClass = BEM("edt-inspect-panel");

	const t = useTranslate();

	const { model, updateRef } = useBlueprintModel(_model, "flowNode");
	const [ activeTab, setActiveTab ] = useState("props");

	const tabs = useMemo(
		() => [
			{
				id: "props",
				// @todo Translate
				// label: "Options",
				icon: "mdi/tune-variant"
			},
			{
				id: "debug",
				// @todo Translate
				// label: "Debug",
				icon: "mdi/bug"
			},
			{
				id: "comment",
				// label: "Comment",
				icon: "mdi/comment-text"
			}
		],
		[]
	);

	const executionResult = useMemo(() => model?.executionResult, [ model, updateRef ]);
	const executionScope = useMemo(() => {
		return createEmptyScope(
			{
				options: executionResult?.resolvedOpts,
				output: executionResult?.outputData,
				inputScope: executionResult?.scope,
				debug: executionResult?.debug,
				executionTimeInMs: executionResult?.executionTimeInMs
			},
			{
				output: executionResult?.outputType
			}
		);
	}, [ executionResult ]);

	const handleDelete = useCallback(() => {
		const item = nodeList.schema.removeItemById(nodeList, model.id, true);
		item.schema.destroy(item);
	}, [ model ]);

	if (!model) {
		return <InvalidModelNode model={_model} />;
	}

	const nodeType = model.schema.opts.nodeTypes[model.type];
	const outputDescriptors = nodeType.resolveOutputs(model);

	if (!nodeType) {
		return (
			<PanelNote>
				<p>
					<T domain="editor" term={termsEditorInspector.errors.flowNodeHasInvalidConfiguration} />
				</p>
			</PanelNote>
		);
	}

	const getComponentLabel = useCallback(
		(model: TGenericModelNode) => {
			if (!model) {
				return undefined;
			}

			if (model["componentHandler"]) {
				return t("editor", model["componentHandler"]?.label);
			}

			return getComponentLabel(model.parent);
		},
		[ t ]
	);

	const componentLabel = getComponentLabel(model);

	return (
		<div className={baseClass.toClassName()}>
			<div className={baseClass.element("back").toClassName()}>
				<ToolbarCommandButton
					icon="mdi/arrow-left"
					tooltip={componentLabel ? "Back to " + componentLabel : "Back"}
					label={componentLabel ? "Back to " + componentLabel : "Back"}
					commandId="eventNode.deselect"
				/>
			</div>
			<div className={baseClass.element("header").toClassName()}>
				<div className={baseClass.element("header-info").toClassName()}>
					<span className={baseClass.element("header-info-icon").toClassName()}>
						<Icon
							name={overrideIcon || nodeType.icon || "mdi/help-circle"}
							size={ICON_SIZE.SMALL}
						/>
					</span>
					<span className={baseClass.element("header-info-name").toClassName()}>
						{overrideLabel ? overrideLabel : <T domain="editor" term={nodeType.label} />}
					</span>
					<ToolbarButton
						icon="mdi/delete"
						tooltip={t("editor", termsEditorInspector.component.delete)}
						onClick={handleDelete}
					/>
					<ToolbarCommandButton
						icon="mdi/help-circle"
						tooltip={t("editor", termsEditorInspector.component.openDocumentation)}
						commandId="referenceGuide.open"
						// @todo Change URL + translation
						commandArgs={[
							{
								name: nodeType.name,
								type: REFERENCE_TYPE.ACTIONS
							}
						]}
					/>
				</div>

				<InspectorTitle model={model.varName} />
			</div>
			<Tabs
				className={baseClass.element("main-tabs").toClassName()}
				items={tabs}
				selectedId={activeTab}
				onSelect={setActiveTab}
				style={TABS_STYLE.COMPACT}
			/>
			<TabbedContainer
				selectedId={activeTab}
				style={TABBED_CONTAINER_TYPE.SLIDE}
				className={baseClass.element("content").toClassName()}
				items={[
					{
						id: "props",
						render: () => (
							<div className={baseClass.element("content-tab").toClassName()}>
								<Inspector model={model?.opts?.template} />
							</div>
						)
					},
					{
						id: "debug",
						render: () => (
							<div className={baseClass.element("content-tab").toClassName()}>
								{executionResult ? (
									<>
										<FieldHeader>
											{/* @todo Translate */}
											<FieldLabel>Activated Output</FieldLabel>
										</FieldHeader>
										<FieldRow>
											<FieldGroup grow={true}>
												<FieldInlineLabel grow={true}>
													{executionResult.activeOutput && outputDescriptors[executionResult.activeOutput]
														? t(
															"editor",
															outputDescriptors[
																executionResult.activeOutput
															].label
														)
														:  "No output."
													} // @todo Translate
												</FieldInlineLabel>
											</FieldGroup>
										</FieldRow>
										<FieldHeader>
											{/* @todo Translate */}
											<FieldLabel>
												Resolved Options, Output &amp; Input Scope
											</FieldLabel>
										</FieldHeader>
										<FieldRow className={baseClass.element("scope-viewer").toClassName()}>
											<FieldGroup grow={true}>
												<FieldContainer style={FIELD_CONTAINER_STYLE.BORDER}>
													<ScopeViewer
														scope={executionScope}
														defaultLevelsExpanded={2}
													/>
												</FieldContainer>
											</FieldGroup>
										</FieldRow>
									</>
								) : (
									<PanelNote>Node hasn't been executed yet.</PanelNote>
								)}
							</div>
						)
					},
					{
						id: "comment",
						render: () => (
							<div className={baseClass.element("content-tab").toClassName()}>
								{/* <Inspector model={model?.comment} /> */}
								{model && (
									<FieldRow>
										<FieldGroup grow={true}>
											<InspectorControlString model={model.comment} multiline />
										</FieldGroup>
									</FieldRow>
								)}
							</div>
						)
					},
				]}
			/>
		</div>
	);
};
